import { Box, Button, Stack } from '@mui/material'
import * as Yup from 'yup'
import { Formik, Form, type FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'

import { PasswordInput, createPasswordValidationSchema, EmailInput, createEmailValidationSchema } from '@dhl/inputs'
import { type PasswordReset, usePostUserPasswordReset } from '@dhl/receiver-user-service-api'

type FormValues = {
  email: string
  newPassword: string
}

const initialValues: FormValues = {
  email: '',
  newPassword: '',
}

type Props = {
  token: string
  setSuccess: (value: boolean) => void
}

export const PasswordResetForm = ({ token, setSuccess }: Props) => {
  const { t } = useTranslation()
  const { mutateAsync: resetPassword } = usePostUserPasswordReset()

  const validationSchema: Yup.Schema<FormValues> = Yup.object({
    email: createEmailValidationSchema(t),
    newPassword: createPasswordValidationSchema(t),
  })

  const onSubmit = async (values: PasswordReset, { setFieldError }: FormikHelpers<PasswordReset>) => {
    try {
      await resetPassword({
        data: { ...values, token },
      })
      setSuccess(true)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response && error.response.data && error.response.data.key) {
        setFieldError('email', t(`account.passwordReset.error.${error.response.data.key}`))
      } else {
        setFieldError('email', t('common.pleaseTryAgainErrorMessage'))
      }
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ isSubmitting }) => (
        <Form>
          <Stack spacing={2}>
            <EmailInput name="email" variant="filled" fullWidth />
            <Box>
              <PasswordInput name="newPassword" variant="filled" fullWidth showStrength />
            </Box>
            <Button
              type="submit"
              data-test="reset-password-button"
              variant="contained"
              fullWidth
              color="primary"
              disabled={isSubmitting}
            >
              <span>{t('account.passwordReset.resetButton')}</span>
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}
